import type {
  CheckboxProps,
  OptionInputProps,
  TextInputProps,
  OutlinedCheckboxGroupProps,
} from "@brexhq/component-library";
import { useRef } from "react";

type ValueType =
  | CheckboxProps["value"]
  | TextInputProps["value"]
  | OptionInputProps<Record<string, any>>["value"]
  | OutlinedCheckboxGroupProps["value"];

/**
 * Track whether an input has been edited by a user.
 */
export default function useDirtyState(value: ValueType, onDirty: () => void) {
  const initialValue = useRef(value);
  const changeCount = useRef(0);
  const isDirty = useRef(false);
  const didTrackDirtyState = useRef(false);

  return {
    isDirty: isDirty,
    updateDirtyState: (nextValue: ValueType) => {
      changeCount.current = changeCount.current + 1;

      // Only track dirty state once when the field becomes dirty
      if (didTrackDirtyState.current) return;

      const nextIsDirty =
        changeCount.current >= 1 &&
        nextValue !== initialValue.current &&
        nextValue !== "" &&
        nextValue !== null &&
        nextValue !== undefined;

      isDirty.current = nextIsDirty;

      if (!isDirty.current) return;

      onDirty();

      didTrackDirtyState.current = true;
    },
  };
}
